import type { FC } from 'react';
import { useHistory } from 'react-router';
import { HStack, Image, Td, Tr, VStack } from '@chakra-ui/react';

import {
  CreditCardStatusCode,
  PaymentCardProductType,
} from '__generated__/GQL';
import { formatAmount } from 'common/formatAmount';
import { getCardAssetSrc } from 'common/paymentCards/utils';
import ProgressBar from 'components/_core/ProgressBar';
import Text from 'components/_core/Text';
import useToast from 'components/ToastProvider/useToast';
import { usePaymentCardEnabledSubscription } from 'features/PaymentCards/pages/PaymentCardList/hooks/usePaymentCardEnabledSubscription';

import * as routes from '../../../../../../routes';
import {
  isCreditCardFragment,
  type PaymentCardListObservableQuery,
} from '../../../../hooks/usePaymentCardsList';
import { PaymentCardListTable_ItemFragment } from '../../../../hooks/usePaymentCardsList/paymentCardsList.gql';
import { PaymentCardListTableRowStatusBadge } from './components/PaymentCardListTableRowStatusBadge';
import locales from './locales';
import { getCardKey, getCardName, isCreatedCard, isPendingCard } from './utils';

interface Props {
  item: PaymentCardListTable_ItemFragment;
  listObservable: PaymentCardListObservableQuery;
  isSelected: boolean;
}

const Cell: typeof Td = (props) => <Td p="space-0" {...props} />;

export const PaymentCardListTableRow: FC<Props> = ({
  isSelected,
  item,
  listObservable,
}) => {
  const history = useHistory();

  const isBudgetCard =
    item.cardProductType === PaymentCardProductType.VirtualBudget;
  const totalPayment = isBudgetCard
    ? item.totalPaymentAll
    : item.totalPaymentWeek;
  const limitPayment = isBudgetCard
    ? item.limitPaymentAll
    : item.limitPaymentWeek;
  const cardName = getCardName(item);
  const pan = isCreditCardFragment(item) ? item.maskedPan.slice(-4) : null;
  const { showToast } = useToast();

  usePaymentCardEnabledSubscription({
    creditCardId: item.creditCardId,
    observable: listObservable,
    onData() {
      showToast({
        message: locales.cardEnabled,
        type: 'success',
      });
    },
    skip: isCreditCardFragment(item),
  });

  const isSelectable =
    isCreditCardFragment(item) &&
    ![
      CreditCardStatusCode.Deactivated,
      CreditCardStatusCode.Stolen,
      CreditCardStatusCode.Lost,
    ].includes(item.status);

  return (
    <Tr
      _hover={{
        backgroundColor: 'grey.100',
      }}
      _selected={{
        outline: '1px solid var(--colors-grey-800)',
      }}
      aria-selected={isSelected && isSelectable}
      borderRadius="inherit"
      cursor="pointer"
      height="76px"
      onClick={() => history.push(routes.getPaymentCardPath(item.creditCardId))}
      pointerEvents={isSelectable ? 'auto' : 'none'}
    >
      <Cell>
        <HStack gap="space-16">
          <Image
            alt=""
            borderRadius="radius-4"
            height={24}
            objectFit="cover"
            src={getCardAssetSrc(item)}
            width={40}
          />
          <VStack
            alignItems="flex-start"
            gap="space-2"
            opacity={isCreatedCard(item) ? 0.3 : 1}
          >
            <Text fontWeight="weight-500" noOfLines={1} variant="primary">
              {cardName}
            </Text>
            {pan ? (
              <Text noOfLines={1} size="sm" variant="secondary">
                {locales.formatString(locales.description, {
                  cardProductType: locales.cardProductType[getCardKey(item)],
                  pan,
                })}
              </Text>
            ) : null}
          </VStack>
        </HStack>
      </Cell>
      <Cell opacity={isCreatedCard(item) ? 0.3 : 1}>
        <Text fontWeight="weight-500" noOfLines={2} variant="primary">
          {item.cardHolderName}
        </Text>
      </Cell>
      <Cell opacity={isPendingCard(item) || isCreatedCard(item) ? 0.3 : 1}>
        <VStack alignItems="flex-start" gap="space-4" maxWidth={178}>
          <Text fontWeight="weight-500" size="xs" variant="primary">
            {locales.formatString(locales.limitsDescription, {
              amount: formatAmount({
                forcePlusSign: false,
                value: totalPayment,
              }),
              maxAmount: formatAmount({
                forcePlusSign: false,
                value: limitPayment,
              }),
            })}
          </Text>

          <ProgressBar
            height="3px"
            ratio={limitPayment === 0 ? 0 : totalPayment / limitPayment}
          />
        </VStack>
      </Cell>
      <Cell textAlign="right">
        <PaymentCardListTableRowStatusBadge paymentCard={item} />
      </Cell>
    </Tr>
  );
};
